// api.ts — thin client for the frozen API contract. All money/rate fields are already
// integer cents/bp by the time they reach here (see scenario.ts / convert.ts) — this
// module does no unit conversion, only HTTP + error normalisation.
import type { ForecastConfig, MonteCarloResult, SimulationResult } from '@etf-cockpit/engine';
import type { BacktestRequestBody } from './scenario.js';
import type { MonteCarloRequestBody } from './montecarlo.js';
import type {
  BacktestMeta,
  CreateFundBody,
  CreateFundResponse,
  FundHistoryResponse,
  FundInfo,
  FundLookupResponse,
  FundsResponse,
  HealthResponse,
  RefreshResponse,
  SessionResponse,
} from './types.js';

export class ApiError extends Error {
  /** true when the request never reached the server (network down, server offline). */
  offline: boolean;
  status: number | undefined;
  /** Raw server-provided error string, if any (e.g. 409 "no cached data"). */
  serverMessage: string | undefined;

  constructor(
    message: string,
    opts: { offline: boolean; status?: number | undefined; serverMessage?: string | undefined },
  ) {
    super(message);
    this.name = 'ApiError';
    this.offline = opts.offline;
    this.status = opts.status;
    this.serverMessage = opts.serverMessage;
  }
}

/** Fired whenever any /api response (other than /api/login itself) comes back 401 — a
 * session that expired or was invalidated mid-use. Wired by app boot to drop back to the
 * login view. Not called for /api/login's own 401 (wrong password), which the login
 * form's own catch block already handles inline. */
let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch {
    throw new ApiError('Offline — Server nicht erreichbar', { offline: true });
  }

  if (!res.ok) {
    if (res.status === 401 && path !== '/api/login') {
      unauthorizedHandler?.();
    }

    let serverMessage: string | undefined;
    try {
      const body = (await res.json()) as { error?: string };
      serverMessage = body.error;
    } catch {
      // non-JSON error body; fall through with just the status
    }
    throw new ApiError(serverMessage ?? `Serverfehler (${res.status})`, {
      offline: false,
      status: res.status,
      serverMessage,
    });
  }

  // A 204 (DELETE /api/funds/:isin's success response) has no body — res.json() would
  // throw on it. Every other endpoint in this app returns a JSON body on success, so this
  // is scoped tightly to the one status code that's genuinely always empty.
  if (res.status === 204) return undefined as T;

  return (await res.json()) as T;
}

export function getFunds(): Promise<FundsResponse> {
  return request<FundsResponse>('/api/funds');
}

/** PREVIEW only — does not insert anything. Drives the "Fonds hinzufügen" confirm step. */
export function getFundLookup(isin: string): Promise<FundLookupResponse> {
  return request<FundLookupResponse>(`/api/funds/lookup?isin=${encodeURIComponent(isin)}`);
}

export function postCreateFund(body: CreateFundBody): Promise<CreateFundResponse> {
  return request<CreateFundResponse>('/api/funds', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function deleteFund(isin: string): Promise<void> {
  return request<void>(`/api/funds/${encodeURIComponent(isin)}`, { method: 'DELETE' });
}

/** Toggles a fund's enabled flag (Daten view switch). Never touches cached data — see
 * PATCH /api/funds/:isin's server-side contract. */
export function patchFundEnabled(isin: string, enabled: boolean): Promise<{ fund: FundInfo }> {
  return request<{ fund: FundInfo }>(`/api/funds/${encodeURIComponent(isin)}`, {
    method: 'PATCH',
    body: JSON.stringify({ enabled }),
  });
}

/** Sets (terBp a number) or clears (terBp null) a fund's TER (Daten view inline editor).
 * `enabled` is untouched — same PATCH endpoint, independent optional fields server-side. */
export function patchFundTer(isin: string, terBp: number | null): Promise<{ fund: FundInfo }> {
  return request<{ fund: FundInfo }>(`/api/funds/${encodeURIComponent(isin)}`, {
    method: 'PATCH',
    body: JSON.stringify({ terBp }),
  });
}

/** Cache-only fund history (prices/distributions/AGE), for the fund-analysis page. */
export function getFundHistory(isin: string): Promise<FundHistoryResponse> {
  return request<FundHistoryResponse>(`/api/funds/${encodeURIComponent(isin)}/history`);
}

export function getHealth(): Promise<HealthResponse> {
  return request<HealthResponse>('/api/health');
}

export function postForecast(config: ForecastConfig): Promise<SimulationResult> {
  return request<SimulationResult>('/api/simulate/forecast', {
    method: 'POST',
    body: JSON.stringify(config),
  });
}

export function postMonteCarlo(body: MonteCarloRequestBody): Promise<MonteCarloResult> {
  return request<MonteCarloResult>('/api/simulate/montecarlo', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface BacktestResponse {
  result: SimulationResult;
  meta: BacktestMeta;
}

export function postBacktest(body: BacktestRequestBody): Promise<BacktestResponse> {
  return request<BacktestResponse>('/api/simulate/backtest', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function postRefresh(): Promise<RefreshResponse> {
  return request<RefreshResponse>('/api/refresh', { method: 'POST' });
}

export function getCoverage(isin: string): Promise<unknown> {
  return request<unknown>(`/api/coverage/${encodeURIComponent(isin)}`);
}

/** Never throws on a "not logged in" response — that IS a valid {authenticated: false}
 * result, not an error, so app boot can always render straight from it. */
export function getSession(): Promise<SessionResponse> {
  return request<SessionResponse>('/api/session');
}

export function postLogin(password: string): Promise<SessionResponse> {
  return request<SessionResponse>('/api/login', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

export function postLogout(): Promise<void> {
  return request<void>('/api/logout', { method: 'POST' });
}
